/**
 * Loan Sizer DTOs (Issue #1438)
 *
 * Zod schemas + inferred types for:
 *   - PlatformSizingPolicy (lifted hardcoded constants — Issue #1340)
 *   - RateTier / PointsTier (per-tenant pricing bands)
 *   - FieldRange + ResolvedSizingPolicySnapshot (real-time inline sizing UX)
 *   - SizingFieldName canonical enum
 *
 * Owned by 33-pricing-engine-service. Consumed by 2-api-gateway tRPC router,
 * 9-admin-portal (editor cards + formula viewer), and 10-business-portal
 * (inline sizing summary + constraint hints).
 */

import { z } from 'zod';
import { LoanSizerType, LoanSizerTypeSchema } from '../enums/sizer.enums';

// ============================================================================
// SIZING FIELD NAMES (canonical)
// ============================================================================

/**
 * Canonical names for every sizer input/output that can be visualized as a
 * range/slider in the inline sizing UI. Used as the key for `field_ranges`
 * and as the `bindingConstraint` value when a field's max/min is the
 * binding limit on a candidate cap.
 */
export const SizingFieldName = {
  // Inputs (editable)
  PURCHASE_PRICE: 'PURCHASE_PRICE',
  REHAB_BUDGET: 'REHAB_BUDGET',
  CONSTRUCTION_BUDGET: 'CONSTRUCTION_BUDGET',
  ARV: 'ARV',
  AS_IS_VALUE: 'AS_IS_VALUE',
  REQUESTED_LOAN: 'REQUESTED_LOAN',
  RATE: 'RATE',
  POINTS: 'POINTS',
  TERM_MONTHS: 'TERM_MONTHS',
  FICO: 'FICO',
  // Derived caps (read-only on the panel)
  INITIAL_LTC_PLUS_HOLDBACK: 'INITIAL_LTC_PLUS_HOLDBACK',
  TOTAL_LTC: 'TOTAL_LTC',
  ARLTV: 'ARLTV',
  TIER_MAX_LOAN: 'TIER_MAX_LOAN',
  // Computed mechanics (display-only)
  PROPOSED_LOAN_AMOUNT: 'PROPOSED_LOAN_AMOUNT',
  CONSTRUCTION_HOLDBACK: 'CONSTRUCTION_HOLDBACK',
  INTEREST_RESERVE: 'INTEREST_RESERVE',
  CASH_OUT_LESS_CLOSING: 'CASH_OUT_LESS_CLOSING',
} as const;
export type SizingFieldName = (typeof SizingFieldName)[keyof typeof SizingFieldName];
export const SizingFieldNameSchema = z.nativeEnum(SizingFieldName);

// ============================================================================
// FIELD RANGE — per-field band shown on the inline sizing panel
// ============================================================================

/**
 * One row in `SizeLoanResponse.field_ranges`. Tells the UI:
 *   - the acceptable band for this field given the rest of the inputs
 *   - whether the current value supports / binds / overshoots
 *   - which other field is the upstream binding constraint, if any
 */
export const FieldRangeStatus = {
  SUPPORTS: 'SUPPORTS', // teal — current value leaves headroom on this field
  BINDS: 'BINDS', // amber — current value is the binding constraint
  OVER: 'OVER', // red — value exceeds max; needs reduction
  UNDER: 'UNDER', // red — value below min; needs increase
  SUGGESTED: 'SUGGESTED', // blue — value not yet provided; band shown as suggestion
} as const;
export type FieldRangeStatus = (typeof FieldRangeStatus)[keyof typeof FieldRangeStatus];
export const FieldRangeStatusSchema = z.nativeEnum(FieldRangeStatus);

export const FieldRangeSchema = z.object({
  field: SizingFieldNameSchema,
  /** Currently entered value; null if not set yet (status will be SUGGESTED). */
  value: z.number().nullable(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  status: FieldRangeStatusSchema,
  /** Hint string explaining WHY this band — e.g., "Capped by 70% ARLTV on $850K ARV". */
  reason: z.string(),
  /**
   * If the binding constraint on this field is upstream (a tier or another
   * input), name it so the UI can render a link/tooltip.
   */
  bindingFrom: SizingFieldNameSchema.nullable().optional(),
});
export type FieldRange = z.infer<typeof FieldRangeSchema>;

// ============================================================================
// PLATFORM SIZING POLICY
// ============================================================================

export const PlatformSizingPolicySchema = z.object({
  id: z.string(),
  /** Null = platform default; set = tenant override. */
  businessId: z.string().nullable(),
  effectiveFrom: z.string(), // ISO datetime
  effectiveTo: z.string().nullable(),
  isActive: z.boolean(),
  minLoan: z.number(),
  holdbackShareWarn: z.number(),
  guFeasibilityTier1: z.number(),
  guFeasibilityOther: z.number(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type PlatformSizingPolicy = z.infer<typeof PlatformSizingPolicySchema>;

export const UpsertPlatformSizingPolicyInputSchema = z.object({
  /** Optional id for update; omitted on create. */
  id: z.string().optional(),
  /** Null/omit = edit platform default; set = create/edit a tenant override. */
  businessId: z.string().nullable().optional(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  minLoan: z.number().nonnegative(),
  holdbackShareWarn: z.number().min(0).max(1),
  guFeasibilityTier1: z.number().nonnegative(),
  guFeasibilityOther: z.number().nonnegative(),
  notes: z.string().nullable().optional(),
});
export type UpsertPlatformSizingPolicyInput = z.infer<typeof UpsertPlatformSizingPolicyInputSchema>;

// ============================================================================
// RATE TIER / POINTS TIER
// ============================================================================

const tierBandRefinement = <T extends { minRate: number; maxRate: number; baseRate: number }>(s: T) =>
  s.minRate <= s.baseRate && s.baseRate <= s.maxRate;

export const RateTierSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  loanType: LoanSizerTypeSchema,
  tier: z.number().int().positive(),
  baseRate: z.number().nonnegative(),
  minRate: z.number().nonnegative(),
  maxRate: z.number().nonnegative(),
  label: z.string().nullable(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type RateTier = z.infer<typeof RateTierSchema>;

export const UpsertRateTierInputSchema = z
  .object({
    id: z.string().optional(),
    loanType: LoanSizerTypeSchema,
    tier: z.number().int().positive(),
    baseRate: z.number().min(0).max(1),
    minRate: z.number().min(0).max(1),
    maxRate: z.number().min(0).max(1),
    label: z.string().nullable().optional(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable().optional(),
    active: z.boolean().default(true),
  })
  .refine(tierBandRefinement, { message: 'minRate <= baseRate <= maxRate must hold' });
export type UpsertRateTierInput = z.infer<typeof UpsertRateTierInputSchema>;

export const PointsTierSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  loanType: LoanSizerTypeSchema,
  tier: z.number().int().positive(),
  basePoints: z.number().nonnegative(),
  minPoints: z.number().nonnegative(),
  maxPoints: z.number().nonnegative(),
  label: z.string().nullable(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
});
export type PointsTier = z.infer<typeof PointsTierSchema>;

export const UpsertPointsTierInputSchema = z
  .object({
    id: z.string().optional(),
    loanType: LoanSizerTypeSchema,
    tier: z.number().int().positive(),
    basePoints: z.number().min(0).max(1),
    minPoints: z.number().min(0).max(1),
    maxPoints: z.number().min(0).max(1),
    label: z.string().nullable().optional(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable().optional(),
    active: z.boolean().default(true),
  })
  .refine((s) => s.minPoints <= s.basePoints && s.basePoints <= s.maxPoints, {
    message: 'minPoints <= basePoints <= maxPoints must hold',
  });
export type UpsertPointsTierInput = z.infer<typeof UpsertPointsTierInputSchema>;

// ============================================================================
// RESOLVED SIZING POLICY SNAPSHOT (formula viewer)
// ============================================================================

/**
 * Worked-example row used by the read-only `/pricing/sizing-policy` page to
 * show "given these inputs, this formula yields this output" without leaving
 * the screen. Expressed as plain strings so the UI can render with i18n /
 * code highlighting.
 */
export const FormulaWorkedExampleRowSchema = z.object({
  label: z.string(),
  formula: z.string(),
  inputs: z.record(z.string(), z.union([z.string(), z.number()])),
  output: z.string(),
});
export type FormulaWorkedExampleRow = z.infer<typeof FormulaWorkedExampleRowSchema>;

export const ResolvedSizingPolicySnapshotSchema = z.object({
  /** The resolved active platform policy (tenant override → platform fallback). */
  platformPolicy: PlatformSizingPolicySchema,
  /** Whether the resolution was a tenant override or the platform default. */
  source: z.enum(['TENANT', 'PLATFORM']),
  /** Active rate tiers for the tenant. */
  rateTiers: z.array(RateTierSchema),
  /** Active points tiers for the tenant. */
  pointsTiers: z.array(PointsTierSchema),
  /**
   * Per-formula human-readable expression + worked example. The set of keys
   * is fixed by the calculator implementation.
   */
  formulas: z.array(
    z.object({
      key: z.string(), // e.g., "INITIAL_LTC_PLUS_HOLDBACK"
      label: z.string(),
      expression: z.string(), // e.g., "min(initialLtc * purchasePrice + rehabBudget, ...)"
      explanation: z.string(),
    }),
  ),
  workedExample: z.array(FormulaWorkedExampleRowSchema),
  /** Non-null when no rate or points tiers are active for this tenant. */
  missingFlags: z.array(z.enum(['MISSING_RATE_TIER', 'MISSING_POINTS_TIER'])),
});
export type ResolvedSizingPolicySnapshot = z.infer<typeof ResolvedSizingPolicySnapshotSchema>;

// ============================================================================
// LIST / DELETE INPUTS (admin CRUD)
// ============================================================================

export const ListRateTiersInputSchema = z.object({
  loanType: LoanSizerTypeSchema.optional(),
  active: z.boolean().optional(),
});
export type ListRateTiersInput = z.infer<typeof ListRateTiersInputSchema>;

export const ListPointsTiersInputSchema = z.object({
  loanType: LoanSizerTypeSchema.optional(),
  active: z.boolean().optional(),
});
export type ListPointsTiersInput = z.infer<typeof ListPointsTiersInputSchema>;

export const DeleteByIdInputSchema = z.object({ id: z.string().min(1) });
export type DeleteByIdInput = z.infer<typeof DeleteByIdInputSchema>;

// ============================================================================
// Re-export the LoanSizerType enum so consumers only need this DTO file.
// ============================================================================
export { LoanSizerType };
